import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import { cancelarGuiaEnviaCom, CancelEnviaComRequest } from '../services/cotizaciones/cancelEnviaComService'
import { cancelarGuiaEnvia, CancelEnviaRequest } from '../services/cotizaciones/cancelEnviaService'
import { Input, Select, Button } from '../components/ui'
import { FormField } from '../components/ui/Form'
import { getFieldIcon } from '../utils/formIcons'
import { XMarkIcon, ExclamationTriangleIcon, CheckCircleIcon, TruckIcon, HashtagIcon, CalendarIcon, ArrowPathIcon } from '@heroicons/react/24/outline'

interface FormData {
  carrier: string
  trackingNumber: string
  folio: string
  ecommerce?: number
  // Campos específicos para Envía (Colvanes)
  fec_captura?: string
}

const CancelarEnvios: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    carrier: '',
    trackingNumber: '',
    folio: '',
    ecommerce: undefined,
    fec_captura: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [showResult, setShowResult] = useState(false)

  // Función para traducir mensajes de error al español
  const translateErrorMessage = (message: string, trackingNumber?: string): string => {
    const translations: { [key: string]: string } = {
      'Shipment is already canceled': 'El envío ya está cancelado',
      'Invalid Option': 'Opción inválida',
      'Shipment not found': 'Envío no encontrado',
      'Unauthorized': 'No autorizado',
      'Internal server error': 'Error interno del servidor',
      'Network error': 'Error de red',
      'Timeout': 'Tiempo de espera agotado'
    }

    let translatedMessage = translations[message] || message

    // Si el mensaje indica que ya está cancelado y tenemos el número de guía, agregarlo
    if (message === 'Shipment is already canceled' && trackingNumber) {
      translatedMessage = `El envío #${trackingNumber} ya está cancelado`
    }

    return translatedMessage
  }

  // Función para convertir y validar fecha al formato dd/mm/yyyy
  const formatDateToDDMMYYYY = (dateString: string): string | null => {
    if (!dateString || !dateString.trim()) {
      return null
    }

    const trimmedDate = dateString.trim()

    // Si ya está en formato dd/mm/yyyy, validar y retornar
    const ddmmYYYYRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/
    const ddmmYYYYMatch = trimmedDate.match(ddmmYYYYRegex)
    if (ddmmYYYYMatch) {
      const day = parseInt(ddmmYYYYMatch[1], 10)
      const month = parseInt(ddmmYYYYMatch[2], 10)
      const year = parseInt(ddmmYYYYMatch[3], 10)
      
      // Validar que la fecha sea válida
      const date = new Date(year, month - 1, day)
      if (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      ) {
        return trimmedDate // Ya está en el formato correcto
      }
    }

    // Intentar convertir desde formato ISO (yyyy-mm-dd)
    const isoRegex = /^(\d{4})-(\d{2})-(\d{2})$/
    const isoMatch = trimmedDate.match(isoRegex)
    if (isoMatch) {
      const year = parseInt(isoMatch[1], 10)
      const month = parseInt(isoMatch[2], 10)
      const day = parseInt(isoMatch[3], 10)
      
      // Validar que la fecha sea válida
      const date = new Date(year, month - 1, day)
      if (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      ) {
        // Convertir a dd/mm/yyyy
        const formattedDay = day.toString().padStart(2, '0')
        const formattedMonth = month.toString().padStart(2, '0')
        return `${formattedDay}/${formattedMonth}/${year}`
      }
    }

    // Intentar parsear como Date object
    const parsedDate = new Date(trimmedDate)
    if (!isNaN(parsedDate.getTime())) {
      const day = parsedDate.getDate().toString().padStart(2, '0')
      const month = (parsedDate.getMonth() + 1).toString().padStart(2, '0')
      const year = parsedDate.getFullYear()
      return `${day}/${month}/${year}`
    }

    // Si no se pudo convertir, retornar null
    return null
  }

  const carriers = [
    { value: 'noventa9Minutos', label: '99 Minutos' },
    { value: 'coordinadora', label: 'Coordinadora' },
    { value: 'deprisa', label: 'Deprisa' },
    { value: 'envia', label: 'Envía (Colvanes)' },
    { value: 'interRapidisimo', label: 'InterRapidisimo' }
  ]

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'ecommerce' ? (value ? parseInt(value) : undefined) : value
    }))
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Limpiar campos específicos de Colvanes si cambia la transportadora
      ...(name === 'carrier' && value !== 'envia' ? { 
        fec_captura: ''
      } : {})
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validaciones básicas
    if (!formData.carrier || !formData.trackingNumber) {
      toast.error('Por favor complete los campos requeridos: Carrier y Número de Guía', { position: 'top-center' })
      return
    }

    // Validaciones específicas para Envía (Colvanes)
    if (formData.carrier === 'envia') {
      if (!formData.fec_captura) {
        toast.error('Para Envía (Colvanes) se requiere la Fecha Captura', { position: 'top-center' })
        return
      }

      // Validar y convertir formato de fecha a dd/mm/yyyy
      const formattedDate = formatDateToDDMMYYYY(formData.fec_captura)
      if (!formattedDate) {
        toast.error('La fecha de captura debe estar en formato dd/mm/aaaa (ej: 01/09/2018)', { position: 'top-center' })
        return
      }
    }

    setIsLoading(true)
    setResult(null)
    setShowResult(false)

    try {
      let response

      if (formData.carrier === 'envia') {
        // Validar y convertir formato de fecha a dd/mm/yyyy (ya validado arriba, pero lo hacemos de nuevo para seguridad)
        const formattedDate = formatDateToDDMMYYYY(formData.fec_captura!)
        
        // Usar endpoint interno para Envía (Colvanes)
        const enviaData: CancelEnviaRequest = {
          numero_guia: formData.trackingNumber,
          usuario_online: 'EMPCAR01',
          fec_captura: formattedDate!,
          num_documentos: '' // Campo no obligatorio, se envía vacío
        }
        response = await cancelarGuiaEnvia(enviaData)
      } else {
        // Usar endpoint de Envia.com para otras transportadoras
        const enviaComData: CancelEnviaComRequest = {
          carrier: formData.carrier,
          trackingNumber: formData.trackingNumber,
          folio: formData.folio,
          ecommerce: formData.ecommerce
        }
        response = await cancelarGuiaEnviaCom(enviaComData)
      }

      setResult(response)
      setShowResult(true)
      
      // Verificar diferentes tipos de errores en la respuesta
      const hasDataError = response.data?.meta === 'error' && response.data?.error
      const hasRespuestaError = response.data?.respuesta && !response.success
      
      if (response.success && !hasDataError && !hasRespuestaError) {
        toast.success('Guía cancelada exitosamente', { icon: '✅', position: 'top-center' })
      } else {
        // Priorizar mensajes específicos del backend
        let rawErrorMessage = 'Error al cancelar la guía'
        
        if (hasRespuestaError) {
          // Mensaje específico de Envía (Colvanes) - data.respuesta
          rawErrorMessage = response.data.respuesta
        } else if (hasDataError) {
          // Mensaje de error de Envia.com - data.error
          rawErrorMessage = response.data.error.message || response.data.error.description
        } else if (response.message) {
          // Mensaje general de la respuesta
          rawErrorMessage = response.message
        }
        
        const translatedMessage = translateErrorMessage(rawErrorMessage, formData.trackingNumber)
        toast.error(translatedMessage, { icon: '⚠️', position: 'top-center' })
      }
    } catch (error: any) {
      console.error('Error cancelando guía:', error)
      const translatedErrorMessage = translateErrorMessage(error.message || 'Error al cancelar la guía', formData.trackingNumber)
      toast.error(translatedErrorMessage, { icon: '⚠️', position: 'top-center' })
      setResult({ success: false, message: translatedErrorMessage })
      setShowResult(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      carrier: '',
      trackingNumber: '',
      folio: '',
      ecommerce: undefined,
      fec_captura: ''
    })
    setResult(null)
    setShowResult(false)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-center mb-2">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600 mr-3" />
            <h2>Cancelar Envíos</h2>
          </div>
          <p className="text-center text-gray-600 text-sm">
            Cancela guías de envío previamente generadas con Envia.com
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Formulario */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center mb-6">
              <TruckIcon className="h-6 w-6 text-gray-700 mr-2" />
              <h2>Información de la Guía</h2>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Carrier */}
              <FormField 
                label="Transportadora" 
                required
                icon={getFieldIcon('transportadora')}
              >
                <Select
                  name="carrier"
                  value={formData.carrier}
                  onChange={handleSelectChange}
                  required
                  options={carriers}
                  placeholder="Seleccionar transportadora"
                />
              </FormField>

              {/* Tracking Number */}
              <FormField 
                label="Número de Guía" 
                required
                icon={getFieldIcon('guia')}
              >
                <Input
                  type="text"
                  name="trackingNumber"
                  value={formData.trackingNumber}
                  onChange={handleInputChange}
                  required
                  placeholder="Ej: 788138721950"
                  leftIcon={HashtagIcon}
                />
              </FormField>

              {/* Campos específicos para Envía (Colvanes) */}
              {formData.carrier === 'envia' && (
                <>
                  <FormField 
                    label="Fecha Captura" 
                    required
                    icon={getFieldIcon('fecha')}
                  >
                    <Input
                      type="date"
                      name="fec_captura"
                      value={formData.fec_captura}
                      onChange={handleInputChange}
                      required
                      leftIcon={CalendarIcon}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Seleccione la fecha usando el calendario o escríbala en formato yyyy-mm-dd
                    </p>
                  </FormField>

                </>
              )}

              {/* Botones */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  type="submit"
                  variant="danger"
                  disabled={isLoading}
                  isLoading={isLoading}
                  leftIcon={XMarkIcon}
                  fullWidth
                  className="sm:flex-1"
                >
                  {isLoading ? 'Cancelando...' : 'Cancelar Guía'}
                </Button>
                
                <Button
                  type="button"
                  variant="secondary"
                  outline
                  onClick={handleReset}
                  leftIcon={ArrowPathIcon}
                  className="sm:w-auto"
                >
                  Limpiar
                </Button>
              </div>
            </form>
          </div>

          {/* Resultado */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center mb-6">
              <CheckCircleIcon className="h-6 w-6 text-gray-700 mr-2" />
              <h2>Resultado de la Operación</h2>
            </div>
            
            {!showResult ? (
              <div className="text-center py-12 text-gray-500">
                <div className="bg-gray-50 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                  <ExclamationTriangleIcon className="h-10 w-10 text-gray-300" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Esperando operación</h3>
                <p className="text-sm">Complete el formulario y haga clic en "Cancelar Guía" para ver el resultado</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Estado */}
                {(() => {
                  const hasDataError = result?.data?.meta === 'error' && result?.data?.error
                  const hasRespuestaError = result?.data?.respuesta && !result?.success
                  const isActuallySuccess = result?.success && !hasDataError && !hasRespuestaError
                  
                  // Obtener el mensaje de error específico
                  let errorMessage = result?.message || 'Error al cancelar la guía'
                  
                  if (hasRespuestaError) {
                    // Priorizar mensaje específico de Envía (Colvanes) - data.respuesta
                    errorMessage = result.data.respuesta
                  } else if (hasDataError) {
                    // Mensaje de error de Envia.com - data.error
                    errorMessage = result.data.error.message || result.data.error.description || errorMessage
                  }
                  
                  return (
                    <div className={`p-4 rounded-lg border-l-4 ${
                      isActuallySuccess 
                        ? 'bg-green-50 border-green-400' 
                        : 'bg-red-50 border-red-400'
                    }`}>
                      <div className="flex items-center">
                        {isActuallySuccess ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
                        ) : (
                          <XMarkIcon className="h-5 w-5 text-red-400 mr-2" />
                        )}
                        <span className={`font-medium ${
                          isActuallySuccess ? 'text-green-800' : 'text-red-800'
                        }`}>
                          {isActuallySuccess ? 'Guía Cancelada Exitosamente' : 'Error al Cancelar Guía'}
                        </span>
                      </div>
                      <p className={`mt-1 text-sm ${
                        isActuallySuccess ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {translateErrorMessage(errorMessage, formData.trackingNumber)}
                      </p>
                    </div>
                  )
                })()}

                {/* Detalles de la respuesta */}
                {result && (
                  <div className="bg-gray-50 rounded-lg p-4 hidden">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Detalles de la Respuesta:</h3>
                    <pre className="text-xs text-gray-600 overflow-auto max-h-64">
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>       
      </div>
    </div>
  )
}

export default CancelarEnvios